<script lang="ts">
	import type { RouteWithGeometry } from '../map/types';
	import { page as pageState } from '$app/state';
	import NoRouteImage from '$assets/route/no-route.svg';
	import RouteErrorImage from '$assets/route/route-error.svg';
	import SkeletonText from '$lib/components/atoms/skeleton/SkeletonText.svelte';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import { buildItineraryTrackingData } from '$lib/services/tracking/googleAnalytics/tracking';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import * as m from '$paraglide/messages';
	import { createEventDispatcher } from 'svelte';
	import { debounce } from 'throttle-debounce';
	import RouteCard from './routeCard/RouteCard.svelte';
	import RouteError from './RouteError.svelte';
	import { type RouteType, RouteTypeMap, RouteTypeOrder } from './types';

	export let isLoading: boolean;
	export let routes: RouteWithGeometry[];
	export let hasErrors: boolean;
	let classname: string = '';
	export { classname as class };
	let container: HTMLElement;

	let scrollLeft = 0;

	const searchParams = getSearchParamsStore();

	const mapLayoutContext = getMapLayoutContext();
	const { moveDrawer, itineraryStore } = mapLayoutContext;

	// eslint-disable-next-line svelte/no-immutable-reactive-statements
	$: ({ itinerary } = itineraryStore);
	$: selectedRoute = $searchParams.selectedRoute ?? 0;

	const dispatch = createEventDispatcher<{
		select: { selectedRoute: number };
		seeDetails: { selectedRoute: number };
	}>();

	function centerRouteCard(selectedRouteIndex: number | undefined) {
		if (selectedRouteIndex === undefined) {
			return;
		}

		// +2 because the first child is the number of routes and the second is the first route card
		const target = container?.querySelectorAll('.item')[selectedRouteIndex];
		target?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
	}

	// use set timeout to prevent computeIndex to be triggered too early
	$: setTimeout(() => centerRouteCard(selectedRoute), 100);

	const computeIndex = (e: UIEvent) => {
		// target is not well typed, we know it is an HTMLElement
		const target = e.target as HTMLElement;

		if (Math.abs(scrollLeft - target.scrollLeft) < 100) {
			return;
		}

		scrollLeft = target.scrollLeft;

		if (routes.length === 0) {
			return;
		}

		if (
			target.scrollWidth !== target.clientWidth
			// 276px is the route card size in mobile
			&& target.clientWidth < 276 * (routes.length - 1)
		) {
			const screenCenter = target.scrollLeft + target.clientWidth / 2;
			const numberOfRoutes = routes.length;
			const value = (numberOfRoutes * screenCenter) / target.scrollWidth;

			$searchParams.selectedRoute = Math.floor(value);
		}
	};

	function getRouteTypes(route: RouteWithGeometry): RouteType[] {
		const result = route.types
			.map(type => RouteTypeMap[type])
			.sort((a, b) => RouteTypeOrder.indexOf(a) - RouteTypeOrder.indexOf(b));
		const shortestIndex = result.indexOf('shortest');
		if (shortestIndex > -1 && shortestIndex !== 1) {
			result.splice(shortestIndex, 1);
			result.splice(1, 0, 'shortest');
		}
		return result;
	}

	function selectRoute(index: number) {
		dispatch('select', { selectedRoute: index });

		$searchParams = {
			...$searchParams,
			selectedRoute: index,
			itinerary: $itinerary,
		};

		track({
			action: 'itinerary_card',
			payload: {
				action: 'selection',
				...buildItineraryTrackingData(pageState),
			},
		});
		moveDrawer('partial');
	}
</script>

<div
	class="routes flex md:flex-col gap-4 md:px-2 {classname}"
	on:scroll={debounce(100, computeIndex)}
	bind:this={container}
>
	{#if isLoading}
		<div class="hidden md:block pt-3.5">
			<SkeletonText minWords={2} maxWords={3} minWordLength={2} maxWordLength={10} />
		</div>
		<div
			class="rd-3 flex p-2 gap-2 overflow-x-scroll md:p-0 md:bg-neutral-weakest md:shadow-smooth-block md:rd-2 md:mb-2 md:flex-col md:gap-0.5 md:overflow-x-inherit"
		>
			<!-- eslint-disable-next-line svelte/require-each-key -->
			{#each { length: 3 } as _}
				<RouteCard routeData={{ isLoading: true, data: undefined }} class="min-w-65 max-w-65" />
			{/each}
		</div>
	{:else if hasErrors}
		<div class="flex justify-center w-full p-2 md:p-0">
			<RouteError
				text={m.route__error()}
				errorImage={RouteErrorImage}
				showRetry
				on:modify
				on:retry
			/>
		</div>
	{:else if routes.length === 0}
		<div class="flex justify-center w-full p-2">
			<RouteError text={m.route__not_found()} errorImage={NoRouteImage} on:modify />
		</div>
	{:else}
		<div class="hidden md:block text-bold text-sm pt-3.5">
			{routes.length === 1
				? m.route__itineraries__one({ count: routes.length })
				: m.route__itineraries__many({ count: routes.length })}
		</div>
		<div
			class="w-full rd-3 flex p-2 gap-2 overflow-x-scroll md:p-0 md:bg-neutral-weakest md:shadow-smooth-block md:rd-2 md:mb-2 md:flex-col md:gap-0.5 md:overflow-x-inherit"
			data-testid="route-results"
		>
			<!-- eslint-disable-next-line svelte/require-each-key -->
			{#each routes as route, index}
				<div class="item {routes.length === 1 ? 'w-full' : 'w-65'}">
					<RouteCard
						routeData={{
							isLoading: false,
							data: {
								duration: route.duration,
								distance: route.routeDistance,
								carbonDioxideEmission: route.carbonDioxideEmission,
								types: routes.length === 1 ? [] : getRouteTypes(route),
								isSelected: routes.length === 1 || selectedRoute === index,
								totalCost: route.totalCost,
								costs: route.costs,
								evData: route.evData || undefined,
								index,
							},
						}}
						on:select={() => selectRoute(index)}
						on:seeDetails={() => dispatch('seeDetails', { selectedRoute: index })}
						class={routes.length === 1 ? 'w-full' : 'min-w-65 max-w-65'}
					/>
				</div>
			{/each}
		</div>
	{/if}
</div>

<style>
	.routes {
		scroll-snap-type: x mandatory;
	}

	.item {
		all: unset;
		scroll-snap-align: center;
	}
</style>
